import { AssistantItems, Chart } from '@repo/shared/dtos/assistant'
import { generateId } from '@repo/shared/uuid/uuid'
import { random } from 'lodash'

import { AnalyticsChatItem } from '@/store/chat'

type ChartColor = {
  backgroundColor: string
  borderColor: string
}

const CHART_COLORS: ChartColor[] = [
  { backgroundColor: 'rgba(255, 99, 132, 0.5)', borderColor: 'rgba(255, 99, 132, 1)' },
  { backgroundColor: 'rgba(54, 162, 235, 0.5)', borderColor: 'rgba(54, 162, 235, 1)' },
  { backgroundColor: 'rgba(255, 206, 86, 0.5)', borderColor: 'rgba(255, 206, 86, 1)' },
  { backgroundColor: 'rgba(75, 192, 192, 0.5)', borderColor: 'rgba(75, 192, 192, 1)' },
  { backgroundColor: 'rgba(153, 102, 255, 0.5)', borderColor: 'rgba(153, 102, 255, 1)' },
  { backgroundColor: 'rgba(255, 159, 64, 0.5)', borderColor: 'rgba(255, 159, 64, 1)' },
  { backgroundColor: 'rgba(201, 203, 207, 0.5)', borderColor: 'rgba(201, 203, 207, 1)' },
  { backgroundColor: 'rgba(0, 204, 102, 0.5)', borderColor: 'rgba(0, 204, 102, 1)' },
  { backgroundColor: 'rgba(255, 102, 255, 0.5)', borderColor: 'rgba(255, 102, 255, 1)' },
  { backgroundColor: 'rgba(255, 153, 0, 0.5)', borderColor: 'rgba(255, 153, 0, 1)' },
  { backgroundColor: 'rgba(0, 102, 255, 0.5)', borderColor: 'rgba(0, 102, 255, 1)' },
  { backgroundColor: 'rgba(204, 51, 255, 0.5)', borderColor: 'rgba(204, 51, 255, 1)' },
  { backgroundColor: 'rgba(51, 204, 255, 0.5)', borderColor: 'rgba(51, 204, 255, 1)' },
  { backgroundColor: 'rgba(255, 102, 102, 0.5)', borderColor: 'rgba(255, 102, 102, 1)' },
  { backgroundColor: 'rgba(102, 255, 204, 0.5)', borderColor: 'rgba(102, 255, 204, 1)' },
  { backgroundColor: 'rgba(0, 128, 128, 0.5)', borderColor: 'rgba(0, 128, 128, 1)' },
  { backgroundColor: 'rgba(255, 204, 153, 0.5)', borderColor: 'rgba(255, 204, 153, 1)' },
  { backgroundColor: 'rgba(255, 102, 0, 0.5)', borderColor: 'rgba(255, 102, 0, 1)' },
  { backgroundColor: 'rgba(102, 0, 204, 0.5)', borderColor: 'rgba(102, 0, 204, 1)' },
  { backgroundColor: 'rgba(204, 0, 102, 0.5)', borderColor: 'rgba(204, 0, 102, 1)' },
]

// Simple deterministic hash function
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0
  }
  return hash
}

// Deterministic rotation based on the key
function getStableChartColors(key: string): ChartColor[] {
  const hash = hashString(key)
  const offset = hash % CHART_COLORS.length

  // Rotate the array deterministically
  return [...CHART_COLORS.slice(offset), ...CHART_COLORS.slice(0, offset)]
}

function getStableChartColorsN(key: string, n: number): ChartColor[] {
  return getStableChartColors(key).slice(0, n)
}

export function completeDatasetsColors(chart: Chart, key: string) {
  const datasets = chart.payload.datasets
  let newDatasets: typeof datasets = []

  // Use case: for Pie charts with only one dataset
  if (datasets.length === 1 && datasets[0] && chart.type === 'pie') {
    const dataset = datasets[0]
    const fallbackColors = getStableChartColorsN(key, dataset.data.length)

    // Check if backgroundColor already exists as an array
    const existingBackgroundColor = Array.isArray(dataset.backgroundColor)
      ? dataset.backgroundColor
      : undefined
    const existingBorderColor = Array.isArray(dataset.borderColor) ? dataset.borderColor : undefined

    // If colors exist, preserve them and fill in undefined values
    if (existingBackgroundColor || existingBorderColor) {
      const backgroundColor: string[] = existingBackgroundColor
        ? existingBackgroundColor.map((color, index) =>
            color !== undefined ? color : fallbackColors[index]?.backgroundColor ?? '',
          )
        : fallbackColors.map((color) => color.backgroundColor)

      const borderColor: string[] = existingBorderColor
        ? existingBorderColor.map((color, index) =>
            color !== undefined ? color : fallbackColors[index]?.borderColor ?? '',
          )
        : fallbackColors.map((color) => color.borderColor)

      newDatasets = [
        {
          ...dataset,
          backgroundColor,
          borderColor,
        },
      ]
    } else {
      // No existing colors, use all fallback colors
      newDatasets = [
        {
          ...dataset,
          backgroundColor: fallbackColors.map((color) => color.backgroundColor),
          borderColor: fallbackColors.map((color) => color.borderColor),
        },
      ]
    }
  } else {
    // Bar and line charts
    const fallbackColors = getStableChartColorsN(key, datasets.length)
    newDatasets = datasets.map((dataset, index) => {
      const fallbackColor = fallbackColors[index]

      // Preserve existing backgroundColor/borderColor if they exist
      // Otherwise use fallback colors
      const backgroundColor =
        dataset.backgroundColor !== undefined && dataset.backgroundColor !== null
          ? dataset.backgroundColor
          : fallbackColor?.backgroundColor
      const borderColor =
        dataset.borderColor !== undefined && dataset.borderColor !== null
          ? dataset.borderColor
          : fallbackColor?.borderColor

      return {
        ...dataset,
        backgroundColor,
        borderColor,
      }
    })
  }

  chart.payload.datasets = newDatasets
}

// This function will complete the chart colors by picking random colors for the datasets
// ONLY if the backgroundColor and borderColor are not set
export function completeInsightDisplayDatasetsColors(
  displayItem: AssistantItems['insightDisplay'],
) {
  if (displayItem.data.type === 'kpi') return

  const displayData = displayItem.data.value

  completeDatasetsColors(displayData, displayItem.data.title)
}

export const pieData: Chart = {
  type: 'pie',
  payload: {
    labels: ['Red', 'Blue', 'Yellow', 'Green', 'Purple', 'Orange'],
    datasets: [
      {
        label: '# of Votes',
        data: [12, 19, 3, 5, 2, 3],
        backgroundColor: [
          'rgba(255, 99, 132, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(75, 192, 192, 0.2)',
          'rgba(153, 102, 255, 0.2)',
          'rgba(255, 159, 64, 0.2)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  },
}

const labels = ['January', 'February', 'March', 'April', 'May', 'June', 'July']
export const lineData: Chart = {
  type: 'line',
  payload: {
    labels,
    datasets: [
      {
        label: 'Dataset 1',
        data: labels.map(() => random(-1000, 1000)),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
      {
        label: 'Dataset 2',
        data: labels.map(() => random(-1000, 1000)),
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  },
}

export const barData: Chart = {
  type: 'bar',
  payload: {
    labels,
    datasets: [
      {
        label: 'Dataset 1',
        data: labels.map(() => random(0, 1000)),
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
      {
        label: 'Dataset 2',
        data: labels.map(() => random(0, 1000)),
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  },
}

export const items: AnalyticsChatItem[] = [
  {
    id: generateId(),
    type: 'message',
    generationId: '',
    content: 'How can I help you?',
    sentAt: new Date().getTime(),
    traceUrl: 'http://example.com/trace/2',
  },
  {
    id: generateId(),
    isFromUser: true,
    type: 'message',
    generationId: '',
    sentAt: new Date().getTime(),
    content: 'Hello, can you provide insights?',
  },
  {
    id: generateId(),
    type: 'message',
    generationId: '',
    content: 'Here is a pie chart for you.',
    sentAt: new Date().getTime(),
    traceUrl: 'http://example.com/trace/2',
  },
  {
    id: generateId(),
    type: 'insightDisplay',
    generationId: '',
    data: {
      title: 'Pie Chart',
      description: 'This is a pie chart',
      type: 'chart',
      value: pieData,
    },
    sentAt: new Date().getTime(),
    traceUrl: 'http://example.com/trace/2',
  },
  {
    id: generateId(),
    type: 'message',
    generationId: '',
    content: 'Here is a line chart for you.',
    sentAt: new Date().getTime(),
    traceUrl: 'http://example.com/trace/2',
  },
  {
    id: generateId(),
    generationId: '',
    type: 'insightDisplay',
    data: {
      type: 'chart',
      title: 'Line Chart',
      description: 'This is a line chart',
      value: lineData,
    },
    sentAt: new Date().getTime(),
    traceUrl: 'http://example.com/trace/2',
  },
  {
    id: generateId(),
    type: 'message',
    generationId: '',
    content: 'Here is a bar chart for you.',
    sentAt: new Date().getTime(),
    traceUrl: 'http://example.com/trace/2',
  },
  {
    id: generateId(),
    generationId: '',
    type: 'insightDisplay',
    data: {
      title: 'Bar Chart',
      description: 'This is a bar chart',
      type: 'chart',
      value: barData,
    },
    sentAt: new Date().getTime(),
    traceUrl: 'http://example.com/trace/2',
  },
]
